/**
 * "The More You Buy, The More You Save": the volume-price tiers for one product row, opened from
 * a trigger next to the row's price. A popover on desktop (`MoreYouBuyMoreYouSaveDesktop.tsx`),
 * a full-screen slide-out on mobile (`MoreYouBuyMoreYouSaveMobile.tsx`); both list the tiers
 * through `MoreYouBuyMoreYouSaveRows.tsx`.
 *
 * Rendered by the catalog rows
 * (`components/Catalog/CatalogProductsDetails/CatalogProductsDetailsMobile.tsx`,
 * `CatalogProductsDetails/Shared/CatalogProductDetailDesktop.tsx`,
 * `CatalogProductsTableView/Helper/TableViewComponents/TableRowPriceUomCell.tsx`, the guided-view
 * cart), the search rows (`components/Search/ListView/ProductSection.tsx`,
 * `Search/Helpers/HelperComponents.tsx`) and the cart's price cell
 * (`components/ShoppingCart/ShoppingCartTable/ShoppingCartTablePriceUOMCell.tsx`). The product
 * page has its own version (`components/Product/ProductDetailMoreYouBuy/`) that reuses the rows
 * and content from this folder.
 *
 * The tiers are computed by the caller with `getPriceDiscountTier` (re-exported from `index.ts`,
 * implemented in `@ryerson/frontend.common`) from the row's pricing line: its
 * `discountPrices` are converted to the row's quantity and price units, and only tiers that
 * start above the current quantity are kept. The Prices API builds `discountPrices` from I2P's
 * weight-break prices in pounds (`web-prices-api/application/src/mappers/pricing.mapper.ts` →
 * `getItemDiscountPricesFromI2p`). This component does no pricing itself.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import MoreYouBuyMoreYouSaveDesktop from "./MoreYouBuyMoreYouSaveDesktop";
import MoreYouBuyMoreYouSaveMobile from "./MoreYouBuyMoreYouSaveMobile";
import {
	DiscountPriceModel,
	MatchingProduct,
	Product,
	ProductSearchModel,
} from "@ryerson/frontend.common";
import {
	MoreYouBuyMoreYouSaveContent,
	MoreYouBuyMoreYouSaveContentProps,
} from "./helpers/MoreYouBuyMoreYouSaveContent";
import { useApplication } from "@ryerson/frontend.application";
import { DefaultUOMDisplay } from "@ryerson/common.uom";

/** Props a product row passes to {@link MoreYouBuyMoreYouSave}. */
export type MoreYouBuyMoreYouSaveProps = {
	/**
	 * Opens the desktop popover. The mobile slide-out ignores it and follows `moreYouBuyExpanded`.
	 */
	trigger: JSX.Element;
	offset?: string; // It should be in px. default is: 42px
	productDetail: Product | ProductSearchModel | MatchingProduct;
	/**
	 * Quantity unit code (for example `PC`); shown through `@ryerson/common.uom` →
	 * `DefaultUOMDisplay`.
	 */
	quantityUom: string;
	/** Pricing unit code the tier prices are in. */
	priceUom: string;
	/** Open state, owned by the row. */
	moreYouBuyExpanded: boolean;
	setMoreYouBuyExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	/** The tiers to list, from `getPriceDiscountTier`: only those above the current quantity. */
	filteredDiscountedPrice: DiscountPriceModel[];
	/** Desktop renders nothing, not even the trigger, when false; mobile ignores it. */
	hasDiscountPrices: boolean;
	/**
	 * Called with `rowIndex` (0 when absent) when the tiers are closed from their own close icons.
	 */
	handleOnToggle?: (rowIndex: number) => void;
	rowIndex?: number;
};

/** Props of the desktop and mobile views: the row's props plus the resolved content and offset. */
export type MoreYouBuyMoreYouSaveChildProps = {
	trigger: JSX.Element;
	offset: string;
	moreYouBuyExpanded: boolean;
	setMoreYouBuyExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	content: MoreYouBuyMoreYouSaveContentProps;
	productDetail: Product | ProductSearchModel | MatchingProduct;
	quantityUom: string;
	priceUom: string;
	filteredDiscountedPrice: DiscountPriceModel[];
	hasDiscountPrices: boolean;
	handleOnToggle?: (rowIndex: number) => void;
	rowIndex?: number;
};

/**
 * Picks the language's labels, turns the quantity unit code into its display name, and renders
 * the mobile view below `lg` and the desktop view from `lg` up.
 */
const MoreYouBuyMoreYouSave: React.FC<MoreYouBuyMoreYouSaveProps> = ({
	trigger,
	offset = "42px",
	productDetail,
	quantityUom,
	priceUom,
	moreYouBuyExpanded,
	setMoreYouBuyExpanded,
	filteredDiscountedPrice,
	hasDiscountPrices,
	handleOnToggle,
	rowIndex,
}) => {
	const {
		localization: { language },
	} = useApplication();

	const content: MoreYouBuyMoreYouSaveContentProps =
		MoreYouBuyMoreYouSaveContent[language] || MoreYouBuyMoreYouSaveContent.en;

	const qtyUOM = DefaultUOMDisplay[quantityUom as keyof typeof DefaultUOMDisplay];

	return (
		<>
			<Media lessThan="lg">
				<MoreYouBuyMoreYouSaveMobile
					trigger={trigger}
					offset={offset}
					moreYouBuyExpanded={moreYouBuyExpanded}
					setMoreYouBuyExpanded={setMoreYouBuyExpanded}
					content={content}
					productDetail={productDetail}
					quantityUom={qtyUOM}
					priceUom={priceUom}
					filteredDiscountedPrice={filteredDiscountedPrice}
					hasDiscountPrices={hasDiscountPrices}
					handleOnToggle={handleOnToggle}
					rowIndex={rowIndex}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<MoreYouBuyMoreYouSaveDesktop
					trigger={trigger}
					offset={offset}
					moreYouBuyExpanded={moreYouBuyExpanded}
					setMoreYouBuyExpanded={setMoreYouBuyExpanded}
					content={content}
					productDetail={productDetail}
					quantityUom={qtyUOM}
					priceUom={priceUom}
					filteredDiscountedPrice={filteredDiscountedPrice}
					hasDiscountPrices={hasDiscountPrices}
					handleOnToggle={handleOnToggle}
					rowIndex={rowIndex}
				/>
			</Media>
		</>
	);
};

export default MoreYouBuyMoreYouSave;
